<template>
  <div class="box">
    <article class="media">
      <div class="media-content">
        <div v-if="items.length > 0" class="content">
          <b-table
            :data="items.length === 0 ? [] : items"
            :striped="true"
            :mobile-cards="true"
            default-sort="name"
          >

            <template slot-scope="props">
              <b-table-column field="name" :label="$t('item.name')" sortable>
                <div class="field">
                  <p class="control is-expanded">
                    <input
                      v-validate="'required'"
                      class="input is-small"
                      name="itemName"
                      type="text"
                      v-model="props.row.name"
                      :placeholder="$t('item.nameExtended')"
                      @keyup.enter="validateBeforeSubmit"
                    >
                  </p>
                </div>
              </b-table-column>

              <b-table-column field="quantity" :label="$t('item.quantity')" numeric>
                <div class="field is-grouped">
                  <p class="control is-expanded">
                    <input
                      v-validate="'required|min_value:0.001|decimal:3'"
                      class="input is-small"
                      name="itemQuantity"
                      size="5"
                      v-model="props.row.quantity"
                      :placeholder="$t('item.quantityExtended')"
                    >
                  </p>
                  <p class="control">
                    <span class="select is-small">
                      <select v-model="props.row.unit" required>
                        <option disabled value="">Unité...</option>
                        <option value="Unit">{{ $t('units.Unit') }}</option>
                        <option value="KG">{{ $t('units.KG') }}</option>
                        <option value="L">{{ $t('units.L') }}</option>
                      </select>
                    </span>
                  </p>
                </div>
              </b-table-column>

              <b-table-column field="type" :label="$t('item.type')">
                <div class="field">
                  <p class="control">
                    <span class="select is-small">
                      <select v-model="props.row.type" required>
                        <option disabled value="">Catégorie...</option>
                        <option value="SWEET">{{ $t('types.SWEET') }}</option>
                        <option value="SAVORY">{{ $t('types.SAVORY') }}</option>
                        <option value="MISCELLANEOUS">{{ $t('types.MISCELLANEOUS') }}</option>
                        <option value="ACCESSORY">{{ $t('types.ACCESSORY') }}</option>
                        <option value="SOFT_DRINK">{{ $t('types.SOFT_DRINK') }}</option>
                        <option value="ALCOHOL">{{ $t('types.ALCOHOL') }}</option>
                      </select>
                    </span>
                  </p>
                </div>
              </b-table-column>

              <b-table-column field="id">
                <a class="button is-small is-rounded is-danger is-outlined" @click="deleteItem(props.row.id)">
                  <span class="icon is-small">
                    <i class="fa fa-times"></i>
                  </span>
                </a>
              </b-table-column>
            </template>
          </b-table>

          <!-- Errors -->
          <span v-show="errors.has('itemName')" class="help is-danger">{{ errors.first('itemName') }}</span>
          <span v-show="errors.has('itemQuantity')" class="help is-danger">{{ errors.first('itemQuantity') }}</span>
        </div>

        <nav>
          <div class="field is-grouped is-grouped-multiline is-pulled-right">
            <p class="control">
              <a class="button is-rounded is-success" @click="addItem">
                <span class="icon"><i class="fa fa-plus"></i></span>
                <span>Item</span>
              </a>
            </p>

            <p class="control">
              <button class="button is-primary" @click="validateBeforeSubmit" :class="{ 'is-loading': isLoading }" :disabled="isLoading || isDemoUser()">
                <span class="icon is-small"><i class="fa fa-check"></i></span>
                <span>{{ $t('validate') }}</span>
              </button>
            </p>

            <p class="control">
              <button class="button is-danger" @click="closeForm">
                <span>{{ $t('cancel') }}</span>
              </button>
            </p>
          </div>
        </nav>
      </div>
    </article>
  </div>
</template>

<script>
  import gql from 'graphql-tag'
  import { getEventById } from '@/shared/queries'

  const uuidv4 = require('uuid/v4')

  export default {
    name: 'item-list-form',
    props: {
      onClose: {
        type: Function,
        required: true
      },
      eventId: {
        type: String,
        required: true
      },
      userId: {
        type: String,
        required: false
      },
      userItems: {
        type: Array,
        required: true
      }
    },
    data () {
      return {
        items: [],
        isLoading: false,
        demoEventId: process.env.DEMO_EVENT_ID
      }
    },
    mounted () {
      let existingItems = []
      for (let item of this.userItems) {
        let existingItem = {
          id: item.id,
          name: item.name,
          quantity: item.quantity ? item.quantity : 1,
          unit: item.unit,
          type: item.type
        }

        existingItems.push(existingItem)
      }
      this.items = existingItems.concat(this.items)

      if (this.items.length <= 0) {
        this.addItem()
      }
    },
    methods: {
      addItem () {
        this.items.push({id: uuidv4(), name: '', quantity: 1, unit: 'Unit', type: 'MISCELLANEOUS'})
      },

      deleteItem (id) {
        for (let i = 0; i < this.items.length; i++) {
          if (this.items[i].id && this.items[i].id === id) {
            this.items.splice(i, 1)
            break
          }
        }
      },

      saveList () {
        if (!this.isItemListUntouched()) {
          this.isLoading = true

          const userId = this.userId
          const eventId = this.eventId
          const items = []

          for (let item of this.items) {
            let itemForUpdate = Object.assign({}, item)
            if (userId) {
              itemForUpdate.eventId = eventId
            }
            itemForUpdate.quantity = +item.quantity
            delete itemForUpdate.id
            items.push(itemForUpdate)
          }

          // Delete
          let itemsToDeleteIds = []
          for (let item of this.userItems) {
            itemsToDeleteIds.push(item.id)
          }

          let deleteQueries = ''
          if (itemsToDeleteIds.length > 0) {
            for (let id of itemsToDeleteIds) {
              deleteQueries += id + ': deleteItem(id: "' + id + '") {id}'
            }
          }

          if (!userId) {
            // If userId is undefined, it means we're updating the predefined items
            this.updateEvent(deleteQueries, eventId, items)
          } else {
            this.updateUser(deleteQueries, userId, eventId, items)
          }
        } else {
          this.$toast.open({message: this.$t('itemListUntouched'), position: 'is-bottom', type: 'is-warning'})

          // The list items hasn't changed, so we close the form without sending a request
          this.onClose()
        }
      },

      closeForm () {
        this.onClose()
      },

      isDemoUser () {
        if (this.eventId === process.env.DEMO_EVENT_ID) {
          if (process.env.DEMO_USERS_IDS.split(' ').includes(this.userId)) {
            return true
          }
        }

        return false
      },

      isItemListUntouched () {
        if (this.items.length === this.userItems.length) {
          return this.items.every((element, index) => {
            return (
              element.name === this.userItems[index].name &&
              element.quantity === this.userItems[index].quantity &&
              element.unit === this.userItems[index].unit &&
              element.type === this.userItems[index].type
            )
          })
        } else {
          return false
        }
      },

      validateBeforeSubmit () {
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.saveList()
            this.$ga && this.$ga.event('Items', 'click', 'add', 'Validate')
            return
          }

          this.$toast.open({message: this.$t('formInvalid'), position: 'is-bottom', type: 'is-danger'})
          this.$ga && this.$ga.exception('Items:click:add:Validate')
        })
      },

      updateUser (deleteQueries, userId, eventId, items) {
        let updateUser = gql`
              mutation updateUser($userId: ID!, $items: [UseritemsItem!]) {
                updateUser(id: $userId, items: $items) {
                  id
                  items {
                    id
                    name
                    quantity
                    unit
                    type
                  }
                }
                ${deleteQueries}
              }
            `

        // Mutation
        this.$apollo.mutate({
          mutation: updateUser,
          variables: {
            userId,
            items
          },
          update: (store, {data}) => {
            const result = store.readQuery({query: getEventById, variables: {id: eventId, userFilter: null}})

            for (let participant of result.Event.participants) {
              if (participant.id === userId) {
                participant.items = data.updateUser.items
              }
            }

            store.writeQuery({query: getEventById, variables: {id: eventId, userFilter: null}, data: result})
          }
        }).then(() => {
          // Result
          this.$toast.open({message: this.$t('saveListSuccess'), position: 'is-bottom', type: 'is-success'})

          this.onClose()
        }).catch((error) => {
          // Error
          this.$ga && this.$ga.exception(error.message || error)

          this.$toast.open({message: this.$t('saveListError'), position: 'is-bottom', type: 'is-danger'})
        }).then(() => {
          this.isLoading = false
        })
      },

      updateEvent (deleteQueries, eventId, items) {
        let updateEvent = gql`
              mutation updateEvent($eventId: ID!, $items: [EventitemsItem!]) {
                updateEvent(id: $eventId, items: $items) {
                  id
                  items {
                    id
                    name
                    quantity
                    unit
                    type
                  }
                }
                ${deleteQueries}
              }
            `

        // Mutation
        this.$apollo.mutate({
          mutation: updateEvent,
          variables: {
            eventId,
            items
          },
          update: (store, {data}) => {
            const result = store.readQuery({query: getEventById, variables: {id: eventId, userFilter: null}})

            result.Event.items = data.updateEvent.items

            store.writeQuery({query: getEventById, variables: {id: eventId, userFilter: null}, data: result})
          }
        }).then(() => {
          // Result
          this.$toast.open({message: this.$t('saveListSuccess'), position: 'is-bottom', type: 'is-success'})

          this.onClose()
        }).catch((error) => {
          // Error
          this.$ga && this.$ga.exception(error.message || error)

          this.$toast.open({message: this.$t('saveListError'), position: 'is-bottom', type: 'is-danger'})
        }).then(() => {
          this.isLoading = false
        })
      }
    }
  }
</script>

<style scoped>
  .media-content {
    overflow: initial;
  }
</style>



// WEBPACK FOOTER //
// src/components/ItemListForm.vue